import { useRef } from 'react';
import { assets } from '@/assets';
import { strings } from '@/content/strings';
import { cssVars } from '@/lib/cssVars';
import { useFitText, type FitTextOptions } from '@/lib/useFitText';
import styles from './ScreenEmblem.module.css';

interface ScreenEmblemProps {
  /** First word of the screen name, white chrome. */
  first: string;
  /** Second word, gold. */
  second: string;
  /** Width in design units. */
  width: number;
}

/** Must match the outline and shadow in ScreenEmblem.module.css. */
const FIT: FitTextOptions = {
  minMargin: 0.1,
  maxMargin: 0.2,
  outlineEm: 0.08,
  shadowEm: 0.06,
  minScaleX: 0.6,
};

function NameWord({ text, tone }: { text: string; tone: 'chrome' | 'gold' }) {
  return (
    <span className={styles.word}>
      <span className={styles.outline} aria-hidden>
        {text}
      </span>
      <span className={styles[tone]}>{text}</span>
    </span>
  );
}

/**
 * The Foe Names emblem with the current screen's name on its plate. The name is fitted to the
 * plate with equal margins, so any copy works.
 */
export function ScreenEmblem({ first, second, width }: ScreenEmblemProps) {
  const emblem = assets.brand.emblem;
  const plateRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLHeadingElement>(null);
  useFitText(plateRef, nameRef, FIT, `${first} ${second}`);

  return (
    <header className={styles.root} style={cssVars({ '--width': width })}>
      <img
        className={styles.emblem}
        src={emblem.url}
        width={emblem.width}
        height={emblem.height}
        alt={strings.common.appName}
        draggable={false}
      />
      <div ref={plateRef} className={styles.plate}>
        <h1 ref={nameRef} className={styles.name}>
          <NameWord text={first} tone="chrome" /> <NameWord text={second} tone="gold" />
        </h1>
      </div>
    </header>
  );
}
